import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CAMPO, CampoDeBusca, Carregando, Recado } from "./ui";
import { dataDoDia } from "./FaculdadeUi";
import { dataHora } from "../lib/admin";
import { baixarCsv, montarCsv } from "../lib/csv";
import {
  ativarCurso,
  emitirAMao,
  frase,
  lerCurso,
  lerCursos,
  lerInscritos,
  linkDaEmissao,
  linkDaPresenca,
  linkDoCertificado,
  revogarCertificado,
  salvarAgenda,
  salvarCurso,
  telefoneNaTela,
  type AulaDaAgenda,
  type CertificadoDoCurso,
  type CursoCompleto,
  type CursoDaLista,
  type DadosDoCurso,
  type Inscrito,
  type MudancaDoCurso,
  type Resposta,
} from "../lib/cursosGratuitosAdmin";

// Admin › Cursos gratuitos (000381): a tela da equipe dos cursos gratuitos ao vivo, com as funções
// da área administrativa do certificados-cf — a lista dos cursos, os dados, a agenda, os links, os
// inscritos e os certificados.
//
// O curso é aberto na QUERY (`?curso=<slug>`, e `?curso=novo` para criar), como as páginas do
// Catálogo: o endereço é o que o operador manda a um colega.
//
// Quem só VÊ a aba recebe a tela sem os formulários e sem os botões de escrita (o aviso do alto é o
// do painel). É conforto, não trava: quem recusa é cada função `cg_admin_*`.

type Recado_ = { tipo: "ok" | "erro"; texto: string } | null;

const BOTAO_SECUNDARIO =
  "min-h-10 rounded-full border border-borda px-4 text-sm font-semibold text-tinta transition hover:border-acento hover:text-titulo disabled:opacity-55";
const BOTAO_PERIGO =
  "min-h-9 rounded-full border border-erro-borda px-3 text-xs font-semibold text-erro-tinta transition hover:bg-erro-fundo disabled:opacity-55";
const TH = "p-2 font-bold";
const POR_PAGINA = 50;

export default function AdminCursosGratuitos() {
  const [busca] = useSearchParams();
  const slug = busca.get("curso");
  if (slug === "novo") return <FormularioDoCurso curso={null} />;
  if (slug) return <PaginaDoCurso key={slug} slug={slug} />;
  return <ListaDeCursos />;
}

// ---------------------------------------------------------------------------
// A lista
// ---------------------------------------------------------------------------
function ListaDeCursos() {
  const [r, setR] = useState<Awaited<ReturnType<typeof lerCursos>> | null>(null);
  useEffect(() => {
    let vivo = true;
    void lerCursos().then((x) => vivo && setR(x));
    return () => {
      vivo = false;
    };
  }, []);

  if (!r) return <Carregando texto="Lendo os cursos gratuitos…" />;
  if (!r.ok || !("cursos" in r)) return <Recado tipo="erro" texto={frase(r as Resposta)} />;

  return (
    <div className="flex flex-col gap-5" data-cursos-gratuitos>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-titulo">Cursos gratuitos</h2>
          <p className="mt-1 text-sm leading-relaxed text-cinza">
            Os cursos gratuitos ao vivo: a lista de presença de cada aula e o certificado de quem assistiu. Até a virada, os
            cursos continuam sendo criados no site de hoje e chegam aqui pela carga; o que a equipe muda aqui fica, e a carga
            não passa por cima.
          </p>
        </div>
        {r.pode_editar && (
          <Link to="?curso=novo" className="pill-cta inline-flex min-h-11 items-center px-5 text-sm" data-novo-curso>
            Novo curso
          </Link>
        )}
      </div>

      <div className="surface overflow-x-auto p-4 sm:p-5">
        <table className="w-full min-w-200 text-sm" data-lista-de-cursos>
          <thead>
            <tr className="border-b border-borda text-left text-xs text-cinza uppercase">
              <th className={TH}>Curso</th>
              <th className={TH}>Data</th>
              <th className={`${TH} text-right`}>Aulas</th>
              <th className={`${TH} text-right`}>Inscritos</th>
              <th className={`${TH} text-right`}>Presenças</th>
              <th className={`${TH} text-right`}>Certificados</th>
              <th className={TH}>Situação</th>
            </tr>
          </thead>
          <tbody>
            {r.cursos.map((c) => (
              <LinhaDoCurso key={c.slug} c={c} />
            ))}
            {r.cursos.length === 0 && (
              <tr>
                <td colSpan={7} className="p-4 text-center text-cinza">
                  Nenhum curso gratuito ainda.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LinhaDoCurso({ c }: { c: CursoDaLista }) {
  return (
    <tr className="border-b border-borda/60" data-curso={c.slug}>
      <td className="p-2">
        <Link to={`?curso=${encodeURIComponent(c.slug)}`} className="font-semibold text-link hover:underline">
          {c.nome}
        </Link>
        <span className="block text-xs text-cinza">{c.slug}</span>
      </td>
      <td className="p-2 whitespace-nowrap text-cinza tabular-nums">{dataDoDia(c.data)}</td>
      <td className="p-2 text-right text-tinta tabular-nums">
        {c.num_aulas}
        {c.aulas_com_agenda < c.num_aulas && (
          <span className="block text-xs text-cinza">{c.aulas_com_agenda === 0 ? "sem agenda" : `${c.aulas_com_agenda} com data`}</span>
        )}
      </td>
      <td className="p-2 text-right text-tinta tabular-nums">{c.inscritos.toLocaleString("pt-BR")}</td>
      <td className="p-2 text-right text-tinta tabular-nums">{c.presencas.toLocaleString("pt-BR")}</td>
      <td className="p-2 text-right text-tinta tabular-nums">
        {c.certificados.toLocaleString("pt-BR")}
        {c.revogados > 0 && <span className="block text-xs text-cinza">{c.revogados} revogado{c.revogados === 1 ? "" : "s"}</span>}
      </td>
      <td className="p-2">
        <ChipSituacao ativo={c.ativo} />
      </td>
    </tr>
  );
}

function ChipSituacao({ ativo }: { ativo: boolean }) {
  return (
    <span
      data-situacao={ativo ? "ativo" : "desativado"}
      className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        ativo ? "bg-aviso-fundo text-aviso-tinta" : "bg-chip text-chip-tinta"
      }`}
    >
      {ativo ? "Ativo" : "Desativado"}
    </span>
  );
}

// ---------------------------------------------------------------------------
// A página de um curso
// ---------------------------------------------------------------------------
function PaginaDoCurso({ slug }: { slug: string }) {
  const [r, setR] = useState<CursoCompleto | Resposta | null>(null);
  const [inscritos, setInscritos] = useState<Inscrito[] | Resposta | null>(null);
  const [versao, setVersao] = useState(0);

  useEffect(() => {
    let vivo = true;
    void Promise.all([lerCurso(slug), lerInscritos(slug)]).then(([c, i]) => {
      if (!vivo) return;
      setR(c);
      setInscritos(i.ok && "inscritos" in i ? i.inscritos : (i as Resposta));
    });
    return () => {
      vivo = false;
    };
  }, [slug, versao]);

  // Na primeira leitura, o loader; nas releituras depois de um ato, a tela fica (a frase do banco
  // não pode sumir com o componente que a mostra — a cicatriz da grade, 26/09).
  if (!r) return <Carregando texto="Abrindo o curso…" />;
  if (!r.ok || !("curso" in r)) {
    return (
      <div className="flex flex-col gap-4">
        <Voltar />
        <Recado tipo="erro" texto={frase(r as Resposta)} />
      </div>
    );
  }
  const reler = () => setVersao((v) => v + 1);
  const { curso, aulas, certificados, registro, pode_editar } = r;

  return (
    <div className="flex flex-col gap-5" data-pagina-do-curso={curso.slug}>
      <Voltar />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-titulo">{curso.nome}</h2>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-cinza">
            <ChipSituacao ativo={curso.ativo} />
            <span>{curso.origem === "certificados-cf" ? "Veio do site de hoje (certificados-cf)" : "Criado no hub"}</span>
            {curso.editado_no_hub_em && <span>· editado no hub em {dataHora(curso.editado_no_hub_em)}</span>}
          </p>
        </div>
        {pode_editar && <AtivarOuDesativar slug={curso.slug} ativo={curso.ativo} aoMudar={reler} />}
      </div>

      {pode_editar ? (
        <FormularioDoCurso curso={curso} temCertificados={certificados.length} aoSalvar={reler} />
      ) : (
        <ResumoDoCurso curso={curso} />
      )}
      <Agenda slug={curso.slug} aulas={aulas} podeEditar={pode_editar} aoSalvar={reler} />
      <Links slug={curso.slug} aulas={aulas} />
      <Inscritos slug={curso.slug} inscritos={inscritos} />
      <Certificados slug={curso.slug} certificados={certificados} podeEditar={pode_editar} aoMudar={reler} />
      <Registro registro={registro} certificados={certificados} />
    </div>
  );
}

function Voltar() {
  return (
    <Link to="?" className="w-fit text-sm font-semibold text-link hover:underline" data-voltar-aos-cursos>
      ← Voltar aos cursos
    </Link>
  );
}

function Bloco({ titulo, descricao, acao, children, ...resto }: { titulo: string; descricao?: ReactNode; acao?: ReactNode; children: ReactNode } & Record<`data-${string}`, string | boolean | undefined>) {
  return (
    <section className="surface p-5 sm:p-6" {...resto}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <h3 className="font-display text-lg font-bold text-titulo">{titulo}</h3>
          {descricao && <p className="mt-1 text-sm leading-relaxed text-cinza">{descricao}</p>}
        </div>
        {acao}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function ResumoDoCurso({ curso }: { curso: CursoCompleto["curso"] }) {
  const linhas: [string, string][] = [
    ["Endereço", curso.slug],
    ["Carga horária", `${curso.carga_horaria} horas`],
    ["Aulas", String(curso.num_aulas)],
    ["Data de conclusão", dataDoDia(curso.data)],
    ["Lista de presença", curso.requer_presenca ? (curso.exige_presenca_total ? "sim, e o certificado pede todas as aulas" : "sim") : "não"],
  ];
  return (
    <Bloco titulo="Dados do curso">
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        {linhas.map(([k, v]) => (
          <div key={k}>
            <dt className="text-xs font-bold tracking-wide text-cinza uppercase">{k}</dt>
            <dd className="text-tinta">{v}</dd>
          </div>
        ))}
      </dl>
    </Bloco>
  );
}

// O formulário serve aos dois gestos: criar (`curso` nulo) e editar. O motivo só aparece quando o
// banco pede (`pede_motivo`), com a frase dele dizendo por quê — a tela não decide quando é preciso.
function FormularioDoCurso({
  curso,
  temCertificados = 0,
  aoSalvar,
}: {
  curso: CursoCompleto["curso"] | null;
  temCertificados?: number;
  aoSalvar?: () => void;
}) {
  const navegar = useNavigate();
  const inicial = (): DadosDoCurso => ({
    slug: curso?.slug ?? "",
    nome: curso?.nome ?? "",
    carga_horaria: curso?.carga_horaria ?? null,
    num_aulas: curso?.num_aulas ?? 1,
    data: curso?.data ?? "",
    requer_presenca: curso?.requer_presenca ?? true,
    exige_presenca_total: curso?.exige_presenca_total ?? false,
  });
  const [d, setD] = useState<DadosDoCurso>(inicial);
  const [motivo, setMotivo] = useState("");
  const [pedeMotivo, setPedeMotivo] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<Recado_>(null);

  // Depois de salvar, o curso relido volta pelas props: o formulário acompanha.
  useEffect(() => {
    setD(inicial());
  }, [curso?.slug, curso?.nome, curso?.carga_horaria, curso?.num_aulas, curso?.data, curso?.requer_presenca, curso?.exige_presenca_total]);

  async function salvar(e: FormEvent) {
    e.preventDefault();
    setSalvando(true);
    setRecado(null);
    const x = await salvarCurso(curso?.slug ?? null, d, pedeMotivo ? motivo : "");
    setSalvando(false);
    if (x.ok) {
      setPedeMotivo(false);
      setMotivo("");
      setRecado({ tipo: "ok", texto: frase(x) });
      const novo = "slug" in x && x.slug ? x.slug : d.slug;
      if (!curso || novo !== curso.slug) navegar(`?curso=${encodeURIComponent(novo)}`);
      else aoSalvar?.();
      return;
    }
    if (x.pede_motivo) setPedeMotivo(true);
    setRecado({ tipo: "erro", texto: frase(x) });
  }

  const num = (v: string) => (v.trim() === "" ? null : Number(v));
  return (
    <Bloco
      titulo={curso ? "Dados do curso" : "Novo curso"}
      descricao={
        curso
          ? temCertificados > 0
            ? "O nome, a carga horária e a data saem impressos no certificado: mudar um deles alcança os que já foram emitidos, e o banco pede o motivo."
            : undefined
          : "O endereço vira o link de presença e de emissão, e não muda depois que alguém assinar a presença."
      }
      data-formulario-do-curso
    >
      {!curso && (
        <Link to="?" className="mb-4 block w-fit text-sm font-semibold text-link hover:underline">
          ← Voltar aos cursos
        </Link>
      )}
      <form onSubmit={salvar} className="grid gap-4 sm:grid-cols-2">
        <Campo rotulo="Nome do curso" largo>
          <input className={CAMPO} value={d.nome} onChange={(e) => setD({ ...d, nome: e.target.value })} data-campo="nome" />
        </Campo>
        <Campo rotulo="Endereço" dica="letras minúsculas, números e hífen">
          <input
            className={CAMPO}
            value={d.slug}
            onChange={(e) => setD({ ...d, slug: e.target.value })}
            placeholder="reforma-tributaria"
            data-campo="slug"
          />
        </Campo>
        <Campo rotulo="Data de conclusão" dica="sai impressa no certificado">
          <input type="date" className={CAMPO} value={d.data} onChange={(e) => setD({ ...d, data: e.target.value })} data-campo="data" />
        </Campo>
        <Campo rotulo="Carga horária (horas)">
          <input
            type="number"
            inputMode="numeric"
            className={CAMPO}
            value={d.carga_horaria ?? ""}
            onChange={(e) => setD({ ...d, carga_horaria: num(e.target.value) })}
            data-campo="carga"
          />
        </Campo>
        <Campo rotulo="Número de aulas">
          <input
            type="number"
            inputMode="numeric"
            className={CAMPO}
            value={d.num_aulas ?? ""}
            onChange={(e) => setD({ ...d, num_aulas: num(e.target.value) })}
            data-campo="num-aulas"
          />
        </Campo>
        <div className="flex flex-col gap-2 text-sm text-tinta sm:col-span-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="size-4 accent-acento"
              checked={d.requer_presenca}
              onChange={(e) => setD({ ...d, requer_presenca: e.target.checked })}
              data-campo="requer-presenca"
            />
            O curso tem lista de presença (o certificado sai só para quem assinou)
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="size-4 accent-acento"
              checked={d.exige_presenca_total}
              onChange={(e) => setD({ ...d, exige_presenca_total: e.target.checked })}
              data-campo="presenca-total"
            />
            O certificado pede presença em todas as aulas
          </label>
        </div>
        {pedeMotivo && (
          <Campo rotulo="Motivo da mudança" largo>
            <input className={CAMPO} value={motivo} onChange={(e) => setMotivo(e.target.value)} data-campo="motivo-do-curso" autoFocus />
          </Campo>
        )}
        <div className="sm:col-span-2">
          <button type="submit" disabled={salvando} className="pill-cta min-h-11 px-6 text-sm disabled:opacity-55" data-salvar-curso>
            {salvando ? "Salvando…" : curso ? "Salvar" : "Criar curso"}
          </button>
          {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
        </div>
      </form>
    </Bloco>
  );
}

function Campo({ rotulo, dica, largo = false, children }: { rotulo: string; dica?: string; largo?: boolean; children: ReactNode }) {
  return (
    <label className={`flex flex-col gap-1.5 text-sm ${largo ? "sm:col-span-2" : ""}`}>
      <span className="font-semibold text-tinta">
        {rotulo}
        {dica && <span className="ml-1 font-normal text-cinza">({dica})</span>}
      </span>
      {children}
    </label>
  );
}

function AtivarOuDesativar({ slug, ativo, aoMudar }: { slug: string; ativo: boolean; aoMudar: () => void }) {
  const [aberto, setAberto] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<Recado_>(null);

  async function mudar(paraAtivo: boolean) {
    setSalvando(true);
    setRecado(null);
    const x = await ativarCurso(slug, paraAtivo, motivo);
    setSalvando(false);
    if (x.ok) {
      setAberto(false);
      setMotivo("");
      aoMudar();
      return;
    }
    setRecado({ tipo: "erro", texto: frase(x) });
  }

  if (ativo && !aberto) {
    return (
      <button type="button" className={BOTAO_PERIGO} onClick={() => setAberto(true)} data-desativar-curso>
        Desativar o curso
      </button>
    );
  }
  if (!ativo) {
    return (
      <div className="max-w-sm">
        <button type="button" className={BOTAO_SECUNDARIO} disabled={salvando} onClick={() => void mudar(true)} data-reativar-curso>
          {salvando ? "Reativando…" : "Reativar o curso"}
        </button>
        {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
      </div>
    );
  }
  return (
    <div className="w-full max-w-md rounded-cf-lg border border-erro-borda p-4" data-confirmar-desativar>
      <p className="text-sm leading-relaxed text-tinta">
        Desativar faz os links de presença e de emissão pararem de funcionar para os alunos. O certificado já emitido continua
        valendo, e dá para reativar depois.
      </p>
      <label className="mt-3 flex flex-col gap-1.5 text-sm">
        <span className="font-semibold text-tinta">Motivo</span>
        <input className={CAMPO} value={motivo} onChange={(e) => setMotivo(e.target.value)} data-campo="motivo-desativar" autoFocus />
      </label>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className={BOTAO_PERIGO} disabled={salvando} onClick={() => void mudar(false)} data-confirmar>
          {salvando ? "Desativando…" : "Desativar"}
        </button>
        <button type="button" className={BOTAO_SECUNDARIO} onClick={() => setAberto(false)}>
          Cancelar
        </button>
      </div>
      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// A agenda
// ---------------------------------------------------------------------------
type LinhaDaAgenda = { numero: number; titulo: string; data: string; hora: string };

function Agenda({
  slug,
  aulas,
  podeEditar,
  aoSalvar,
}: {
  slug: string;
  aulas: CursoCompleto["aulas"];
  podeEditar: boolean;
  aoSalvar: () => void;
}) {
  const doBanco = (): LinhaDaAgenda[] =>
    aulas.map((a) => ({ numero: a.numero, titulo: a.titulo ?? "", data: a.data ?? "", hora: a.hora ?? "" }));
  const [linhas, setLinhas] = useState<LinhaDaAgenda[]>(doBanco);
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<Recado_>(null);
  const chaveDoBanco = JSON.stringify(aulas.map((a) => [a.numero, a.titulo, a.data, a.hora]));
  useEffect(() => {
    setLinhas(doBanco());
  }, [chaveDoBanco]);

  async function salvar() {
    setSalvando(true);
    setRecado(null);
    // Vai a aula que tem QUALQUER campo preenchido; a vazia sai da agenda. A que está pela metade vai
    // como está, e quem diz o que falta é o banco.
    const lista: AulaDaAgenda[] = linhas
      .filter((l) => l.data.trim() || l.hora.trim() || l.titulo.trim())
      .map((l) => ({ numero: l.numero, titulo: l.titulo, data: l.data, hora: l.hora }));
    const x = await salvarAgenda(slug, lista);
    setSalvando(false);
    setRecado({ tipo: x.ok ? "ok" : "erro", texto: frase(x) });
    if (x.ok && !x.nada_mudou) aoSalvar();
  }

  const mudar = (i: number, campo: keyof LinhaDaAgenda, v: string) =>
    setLinhas((ls) => ls.map((l, k) => (k === i ? { ...l, [campo]: v } : l)));

  return (
    <Bloco
      titulo="Agenda das aulas"
      descricao="A data, o horário e o título que a página de presença mostra. A aula sem data continua existindo: a página só não diz quando ela é."
      data-agenda
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-150 text-sm">
          <thead>
            <tr className="border-b border-borda text-left text-xs text-cinza uppercase">
              <th className={TH}>Aula</th>
              <th className={TH}>Data</th>
              <th className={TH}>Horário</th>
              <th className={TH}>Título</th>
              <th className={`${TH} text-right`}>Presenças</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l, i) => (
              <tr key={l.numero} className="border-b border-borda/60" data-aula={l.numero}>
                <td className="p-2 font-semibold text-tinta tabular-nums">{l.numero}</td>
                {podeEditar ? (
                  <>
                    <td className="p-2">
                      <input type="date" aria-label={`Data da aula ${l.numero}`} className={`${CAMPO} h-10`} value={l.data} onChange={(e) => mudar(i, "data", e.target.value)} data-campo="data" />
                    </td>
                    <td className="p-2">
                      <input aria-label={`Horário da aula ${l.numero}`} className={`${CAMPO} h-10`} value={l.hora} placeholder="19h" onChange={(e) => mudar(i, "hora", e.target.value)} data-campo="hora" />
                    </td>
                    <td className="p-2">
                      <input aria-label={`Título da aula ${l.numero}`} className={`${CAMPO} h-10`} value={l.titulo} onChange={(e) => mudar(i, "titulo", e.target.value)} data-campo="titulo" />
                    </td>
                  </>
                ) : (
                  <>
                    <td className="p-2 text-cinza tabular-nums">{l.data ? dataDoDia(l.data) : "—"}</td>
                    <td className="p-2 text-cinza">{l.hora || "—"}</td>
                    <td className="p-2 text-cinza">{l.titulo || "—"}</td>
                  </>
                )}
                <td className="p-2 text-right text-tinta tabular-nums">{aulas[i]?.presencas.toLocaleString("pt-BR") ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {podeEditar && (
        <div className="mt-4">
          <button type="button" className="pill-cta min-h-10 px-5 text-sm disabled:opacity-55" disabled={salvando} onClick={() => void salvar()} data-salvar-agenda>
            {salvando ? "Salvando…" : "Salvar a agenda"}
          </button>
          {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
        </div>
      )}
    </Bloco>
  );
}

// ---------------------------------------------------------------------------
// Os links
// ---------------------------------------------------------------------------
function Links({ slug, aulas }: { slug: string; aulas: CursoCompleto["aulas"] }) {
  return (
    <Bloco
      titulo="Links"
      descricao="Os links do hub. Até a virada, as aulas continuam usando os do site de hoje: na virada, o endereço de lá passa a levar para estes."
      data-links
    >
      <ul className="flex flex-col gap-2">
        {aulas.map((a) => (
          <LinhaDeLink key={a.numero} rotulo={`Presença da aula ${a.numero}`} link={linkDaPresenca(slug, a.numero)} />
        ))}
        <LinhaDeLink rotulo="Emissão do certificado" link={linkDaEmissao(slug)} />
      </ul>
    </Bloco>
  );
}

function LinhaDeLink({ rotulo, link }: { rotulo: string; link: string }) {
  const [copiado, setCopiado] = useState(false);
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-cf border border-borda px-3 py-2" data-link={link}>
      <span className="w-44 shrink-0 text-sm font-semibold text-tinta">{rotulo}</span>
      <code className="min-w-0 flex-1 truncate text-xs text-cinza" title={link}>
        {link}
      </code>
      <button
        type="button"
        className={BOTAO_SECUNDARIO}
        onClick={() => {
          void navigator.clipboard?.writeText(link).then(() => {
            setCopiado(true);
            setTimeout(() => setCopiado(false), 1500);
          });
        }}
      >
        {copiado ? "Copiado" : "Copiar"}
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Os inscritos
// ---------------------------------------------------------------------------
function semAcento(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

function Inscritos({ slug, inscritos }: { slug: string; inscritos: Inscrito[] | Resposta | null }) {
  const [busca, setBusca] = useState("");
  const [mostrar, setMostrar] = useState(POR_PAGINA);
  const lista = Array.isArray(inscritos) ? inscritos : null;
  const filtrados = useMemo(() => {
    if (!lista) return [];
    const t = semAcento(busca.trim());
    return t ? lista.filter((i) => semAcento(`${i.nome} ${i.email}`).includes(t)) : lista;
  }, [lista, busca]);

  function exportar() {
    if (!lista) return;
    const csv = montarCsv(
      ["Nome", "E-mail", "Telefone", "Aulas assinadas", "Quantas aulas", "Primeira presença", "Última presença", "Certificado"],
      lista.map((i) => [
        i.nome,
        i.email,
        telefoneNaTela(i.telefone),
        i.aulas.join(", "),
        i.aulas.length,
        dataHora(i.primeira),
        dataHora(i.ultima),
        i.certificado === "valido" ? "sim" : i.certificado === "revogado" ? "revogado" : "não",
      ]),
    );
    baixarCsv(csv, `inscritos-${slug}.csv`);
  }

  return (
    <Bloco
      titulo={lista ? `Inscritos (${lista.length.toLocaleString("pt-BR")})` : "Inscritos"}
      descricao="Quem assinou a presença em pelo menos uma aula, com o nome e o telefone da presença mais recente."
      acao={
        lista && lista.length > 0 ? (
          <button type="button" className={BOTAO_SECUNDARIO} onClick={exportar} data-exportar-inscritos>
            Exportar CSV
          </button>
        ) : undefined
      }
      data-inscritos
    >
      {!inscritos && <p className="text-sm text-cinza">Lendo os inscritos…</p>}
      {inscritos && !lista && <Recado tipo="erro" texto={frase(inscritos as Resposta)} />}
      {lista && lista.length > 0 && (
        <>
          <CampoDeBusca
            valor={busca}
            aoMudar={(v) => {
              setBusca(v);
              setMostrar(POR_PAGINA);
            }}
            aoLimpar={() => setBusca("")}
            placeholder="Buscar por nome ou e-mail"
            rotulo="Buscar entre os inscritos"
            className="max-w-md"
            data-busca-inscritos
          />
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-200 text-sm">
              <thead>
                <tr className="border-b border-borda text-left text-xs text-cinza uppercase">
                  <th className={TH}>Nome</th>
                  <th className={TH}>E-mail</th>
                  <th className={TH}>Telefone</th>
                  <th className={TH}>Aulas</th>
                  <th className={TH}>Última presença</th>
                  <th className={TH}>Certificado</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.slice(0, mostrar).map((i) => (
                  <tr key={i.email} className="border-b border-borda/60" data-inscrito={i.email}>
                    <td className="p-2 text-tinta">{i.nome}</td>
                    <td className="p-2 text-cinza">{i.email}</td>
                    <td className="p-2 whitespace-nowrap text-cinza tabular-nums">{telefoneNaTela(i.telefone)}</td>
                    <td className="p-2 text-tinta tabular-nums">{i.aulas.join(", ")}</td>
                    <td className="p-2 whitespace-nowrap text-cinza tabular-nums">{dataHora(i.ultima)}</td>
                    <td className="p-2 text-cinza">{i.certificado === "valido" ? "Sim" : i.certificado === "revogado" ? "Revogado" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Paginacao mostrando={Math.min(mostrar, filtrados.length)} total={filtrados.length} aoMostrarMais={() => setMostrar((m) => m + POR_PAGINA)} />
        </>
      )}
      {lista && lista.length === 0 && <p className="text-sm text-cinza">Ninguém assinou a presença deste curso ainda.</p>}
    </Bloco>
  );
}

function Paginacao({ mostrando, total, aoMostrarMais }: { mostrando: number; total: number; aoMostrarMais: () => void }) {
  if (total === 0) return <p className="mt-3 text-sm text-cinza">Nenhum resultado para esta busca.</p>;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-cinza" data-paginacao>
      <span>
        Mostrando {mostrando.toLocaleString("pt-BR")} de {total.toLocaleString("pt-BR")}
      </span>
      {mostrando < total && (
        <button type="button" className={BOTAO_SECUNDARIO} onClick={aoMostrarMais} data-mostrar-mais>
          Mostrar mais {Math.min(POR_PAGINA, total - mostrando)}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Os certificados
// ---------------------------------------------------------------------------
const ROTULO_DA_ORIGEM: Record<CertificadoDoCurso["origem"], string> = {
  "certificados-cf": "site de hoje",
  pagina: "página do hub",
  equipe: "à mão, pela equipe",
};

function Certificados({
  slug,
  certificados,
  podeEditar,
  aoMudar,
}: {
  slug: string;
  certificados: CertificadoDoCurso[];
  podeEditar: boolean;
  aoMudar: () => void;
}) {
  const [busca, setBusca] = useState("");
  const [mostrar, setMostrar] = useState(POR_PAGINA);
  const filtrados = useMemo(() => {
    const t = semAcento(busca.trim());
    return t ? certificados.filter((c) => semAcento(`${c.nome} ${c.email}`).includes(t)) : certificados;
  }, [certificados, busca]);
  const validos = certificados.filter((c) => !c.revogado).length;

  return (
    <Bloco
      titulo={`Certificados (${validos.toLocaleString("pt-BR")})`}
      descricao={
        certificados.length > validos
          ? `${certificados.length - validos} revogado${certificados.length - validos === 1 ? "" : "s"} na lista, marcados como tal.`
          : undefined
      }
      data-certificados
    >
      {podeEditar && <EmitirAMao slug={slug} aoEmitir={aoMudar} />}
      {certificados.length > 0 ? (
        <>
          <CampoDeBusca
            valor={busca}
            aoMudar={(v) => {
              setBusca(v);
              setMostrar(POR_PAGINA);
            }}
            aoLimpar={() => setBusca("")}
            placeholder="Buscar por nome ou e-mail"
            rotulo="Buscar entre os certificados"
            className="mt-4 max-w-md"
            data-busca-certificados
          />
          <ul className="mt-3 flex flex-col divide-y divide-borda/60" data-lista-de-certificados>
            {filtrados.slice(0, mostrar).map((c) => (
              <LinhaDoCertificado key={c.token} c={c} podeEditar={podeEditar} aoMudar={aoMudar} />
            ))}
          </ul>
          <Paginacao mostrando={Math.min(mostrar, filtrados.length)} total={filtrados.length} aoMostrarMais={() => setMostrar((m) => m + POR_PAGINA)} />
        </>
      ) : (
        <p className="mt-4 text-sm text-cinza">Nenhum certificado emitido.</p>
      )}
    </Bloco>
  );
}

function EmitirAMao({ slug, aoEmitir }: { slug: string; aoEmitir: () => void }) {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<Recado_>(null);
  const [token, setToken] = useState<string | null>(null);

  async function emitir(e: FormEvent) {
    e.preventDefault();
    setSalvando(true);
    setRecado(null);
    setToken(null);
    const x = await emitirAMao(slug, nome, email, motivo);
    setSalvando(false);
    setRecado({ tipo: x.ok ? "ok" : "erro", texto: frase(x) });
    if (x.ok && "token" in x && x.token) {
      setToken(x.token);
      if (!x.ja_emitido) {
        setNome("");
        setEmail("");
        setMotivo("");
        aoEmitir();
      }
    }
  }

  return (
    <details className="rounded-cf-lg border border-borda p-4" data-emitir-a-mao>
      <summary className="cursor-pointer text-sm font-semibold text-titulo">Emitir um certificado à mão</summary>
      <p className="mt-2 text-sm leading-relaxed text-cinza">
        Para quem assistiu e não tem a presença registrada. Passa por cima da regra da presença, e por isso pede o motivo. Se o
        e-mail já tem certificado neste curso, volta o mesmo.
      </p>
      <form onSubmit={emitir} className="mt-3 grid gap-3 sm:grid-cols-2">
        <Campo rotulo="Nome completo" dica="como vai sair no certificado">
          <input className={CAMPO} value={nome} onChange={(e) => setNome(e.target.value)} data-campo="nome-a-mao" />
        </Campo>
        <Campo rotulo="E-mail">
          <input type="email" className={CAMPO} value={email} onChange={(e) => setEmail(e.target.value)} data-campo="email-a-mao" />
        </Campo>
        <Campo rotulo="Motivo" largo>
          <input className={CAMPO} value={motivo} onChange={(e) => setMotivo(e.target.value)} data-campo="motivo-a-mao" />
        </Campo>
        <div className="sm:col-span-2">
          <button type="submit" className="pill-cta min-h-10 px-5 text-sm disabled:opacity-55" disabled={salvando} data-emitir>
            {salvando ? "Emitindo…" : "Emitir"}
          </button>
          {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
          {token && (
            <a href={linkDoCertificado(token)} target="_blank" rel="noreferrer" className="mt-2 inline-block text-sm font-semibold text-link hover:underline" data-ver-emitido>
              Ver o certificado<span className="sr-only"> (abre em outra aba)</span>
            </a>
          )}
        </div>
      </form>
    </details>
  );
}

function LinhaDoCertificado({ c, podeEditar, aoMudar }: { c: CertificadoDoCurso; podeEditar: boolean; aoMudar: () => void }) {
  const [revogando, setRevogando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<Recado_>(null);

  async function mudar(revogar: boolean) {
    setSalvando(true);
    setRecado(null);
    const x = await revogarCertificado(c.token, revogar, motivo);
    setSalvando(false);
    if (x.ok) {
      setRevogando(false);
      setMotivo("");
      aoMudar();
      return;
    }
    setRecado({ tipo: "erro", texto: frase(x) });
  }

  return (
    <li className="py-3" data-certificado={c.token} data-revogado={c.revogado ? "sim" : "nao"}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={`font-semibold ${c.revogado ? "text-cinza line-through" : "text-tinta"}`}>{c.nome}</p>
          <p className="text-sm text-cinza">
            {c.email} · emitido em {dataHora(c.emitido_em)} · {ROTULO_DA_ORIGEM[c.origem]}
          </p>
          <p className="mt-1 flex flex-wrap gap-1.5">
            {c.revogado && (
              <span className="rounded-full bg-erro-fundo px-2.5 py-0.5 text-xs font-semibold text-erro-tinta" data-chip-revogado>
                Revogado
              </span>
            )}
            {c.repetido && (
              <span className="rounded-full bg-chip px-2.5 py-0.5 text-xs font-semibold text-chip-tinta" data-chip-repetido>
                O e-mail tem outro certificado neste curso
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a href={linkDoCertificado(c.token)} target="_blank" rel="noreferrer" className="text-sm font-semibold text-link hover:underline">
            Ver<span className="sr-only"> o certificado (abre em outra aba)</span>
          </a>
          {podeEditar &&
            (c.revogado ? (
              <button type="button" className={BOTAO_SECUNDARIO} disabled={salvando} onClick={() => void mudar(false)} data-reativar>
                {salvando ? "Reativando…" : "Reativar"}
              </button>
            ) : (
              !revogando && (
                <button type="button" className={BOTAO_PERIGO} onClick={() => setRevogando(true)} data-revogar>
                  Revogar
                </button>
              )
            ))}
        </div>
      </div>
      {revogando && (
        <div className="mt-3 flex flex-wrap items-end gap-2" data-confirmar-revogar>
          <label className="flex min-w-60 flex-1 flex-col gap-1.5 text-sm">
            <span className="font-semibold text-tinta">Motivo da revogação</span>
            <input className={CAMPO} value={motivo} onChange={(e) => setMotivo(e.target.value)} data-campo="motivo-revogar" autoFocus />
          </label>
          <button type="button" className={BOTAO_PERIGO} disabled={salvando} onClick={() => void mudar(true)} data-confirmar>
            {salvando ? "Revogando…" : "Revogar"}
          </button>
          <button type="button" className={BOTAO_SECUNDARIO} onClick={() => setRevogando(false)}>
            Cancelar
          </button>
        </div>
      )}
      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
    </li>
  );
}

// ---------------------------------------------------------------------------
// O registro
// ---------------------------------------------------------------------------
const CAMPOS_DO_CURSO: Record<string, string> = {
  slug: "endereço",
  nome: "nome",
  carga_horaria: "carga horária",
  num_aulas: "aulas",
  data: "data",
  requer_presenca: "lista de presença",
  exige_presenca_total: "presença em todas",
  ativo: "ativo",
  revogado: "revogado",
};

function valorNaTela(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "sim" : "não";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) return dataDoDia(v);
  return String(v);
}

function OQueMudou({ m, certificados }: { m: MudancaDoCurso; certificados: Map<string, CertificadoDoCurso> }) {
  if (m.alvo === "agenda") {
    const n = Array.isArray(m.depois) ? m.depois.length : 0;
    return <>a agenda ficou com {n === 1 ? "1 aula com data" : `${n} aulas com data`}</>;
  }
  if (m.acao === "criou") return <>o curso foi criado</>;
  if (m.acao === "emitiu à mão") {
    const d = (m.depois ?? {}) as Record<string, unknown>;
    return <>certificado de {String(d.nome ?? "")} ({String(d.email ?? "")})</>;
  }
  // Revogar e reativar: o verbo já diz o que mudou; o que falta é DE QUEM era o certificado. O
  // registro guarda o token, e o nome sai da lista que a página já tem.
  if (m.alvo === "certificado") {
    const c = certificados.get(m.alvo_ref);
    return c ? <>certificado de {c.nome} ({c.email})</> : <>certificado {m.alvo_ref}</>;
  }
  const antes = (m.antes ?? {}) as Record<string, unknown>;
  const depois = (m.depois ?? {}) as Record<string, unknown>;
  const chaves = Object.keys(depois);
  if (chaves.length === 0) return null;
  return (
    <>
      {chaves.map((k, i) => (
        <span key={k}>
          {i > 0 && "; "}
          {CAMPOS_DO_CURSO[k] ?? k}: {valorNaTela(antes[k])} → {valorNaTela(depois[k])}
        </span>
      ))}
    </>
  );
}

function Registro({ registro, certificados }: { registro: MudancaDoCurso[]; certificados: CertificadoDoCurso[] }) {
  const [todos, setTodos] = useState(false);
  const porToken = useMemo(() => new Map(certificados.map((c) => [c.token, c])), [certificados]);
  if (registro.length === 0) {
    return (
      <Bloco titulo="Registro" data-registro>
        <p className="text-sm text-cinza">Ninguém mudou este curso pelo hub ainda.</p>
      </Bloco>
    );
  }
  const mostrados = todos ? registro : registro.slice(0, 10);
  return (
    <Bloco titulo="Registro" descricao="Quem mudou o quê pelo hub, do mais novo para o mais antigo." data-registro>
      <ul className="flex flex-col divide-y divide-borda/60">
        {mostrados.map((m, i) => (
          <li key={i} className="py-2.5 text-sm" data-mudanca={m.acao}>
            <p className="text-tinta">
              <strong className="font-semibold">{m.por}</strong> {m.acao} — <OQueMudou m={m} certificados={porToken} />
            </p>
            <p className="text-xs text-cinza">
              {dataHora(m.em)}
              {m.motivo && <> · motivo: {m.motivo}</>}
            </p>
          </li>
        ))}
      </ul>
      {registro.length > 10 && !todos && (
        <button type="button" className={`${BOTAO_SECUNDARIO} mt-3`} onClick={() => setTodos(true)}>
          Mostrar os {registro.length - 10} mais antigos
        </button>
      )}
    </Bloco>
  );
}
